import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CreatePromotionInput } from "@/services/admin/packages.interfaces";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

interface CreatePromotionDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreatePromotionInput) => Promise<void>;
  isSubmitting: boolean;
}

const formatDateToISO = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

const getDefaultEndDate = (): Date => {
  const date = new Date();
  date.setDate(date.getDate() + 5);
  return date;
};

const CreatePromotionDialog: React.FC<CreatePromotionDialogProps> = ({
  isOpen,
  onOpenChange,
  onSubmit,
  isSubmitting,
}) => {
  const [hasStartDate, setHasStartDate] = useState(true);
  const [hasEndDate, setHasEndDate] = useState(true);
  const [isActive, setIsActive] = useState(false); // Changed to false
  const [startDate, setStartDate] = useState(formatDateToISO(new Date()));
  const [endDate, setEndDate] = useState(formatDateToISO(getDefaultEndDate()));
  const [price, setPrice] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [intervalMonths, setIntervalMonths] = useState("1");
  const [recurrencePrice, setRecurrencePrice] = useState("");

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setHasStartDate(true);
      setHasEndDate(true);
      setIsActive(false); // Changed to false
      setStartDate(formatDateToISO(new Date()));
      setEndDate(formatDateToISO(getDefaultEndDate()));
      setPrice("");
      setIsRecurring(false);
      setIntervalMonths("1");
      setRecurrencePrice("");
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (price) {
      const months = parseInt(intervalMonths, 10);
      const promotionData: CreatePromotionInput = {
        price: parseFloat(price),
        startDate: hasStartDate ? new Date(startDate) : null,
        endDate: hasEndDate ? new Date(endDate) : null,
        active: isActive,
        isRecurring,
        recurrenceIntervalMonths: isRecurring && months >= 1 ? months : null,
        recurrencePrice:
          isRecurring && recurrencePrice
            ? parseFloat(recurrencePrice)
            : isRecurring
              ? parseFloat(price)
              : null,
      };

      await onSubmit(promotionData);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Nova Promoção</DialogTitle>
          <DialogDescription>
            Configure o período e valor para promover este pacote
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-5">
            <div className="flex items-start space-x-3">
              <div className="flex h-5 items-center pt-0.5">
                <Checkbox
                  id="active"
                  checked={isActive}
                  onCheckedChange={(checked: boolean) => setIsActive(checked)}
                />
              </div>
              <div className="grid gap-1.5 leading-none">
                <Label htmlFor="active" className="text-sm font-medium">
                  Promoção ativa
                </Label>
                <p className="text-sm text-gray-500">
                  Quando ativa, a promoção será aplicada durante o período configurado
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex h-5 items-center pt-0.5">
                <Checkbox
                  id="hasStartDate"
                  checked={hasStartDate}
                  onCheckedChange={(checked: boolean) => setHasStartDate(checked)}
                />
              </div>
              <div className="grid gap-1.5 leading-none flex-1">
                <Label htmlFor="hasStartDate" className="text-sm font-medium">
                  Definir data inicial
                </Label>
                <p className="text-sm text-gray-500">
                  A partir de quando a promoção estará disponível
                </p>
                {hasStartDate && (
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="mt-1.5"
                    min={formatDateToISO(new Date())}
                    required
                  />
                )}
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex h-5 items-center pt-0.5">
                <Checkbox
                  id="hasEndDate"
                  checked={hasEndDate}
                  onCheckedChange={(checked: boolean) => setHasEndDate(checked)}
                />
              </div>
              <div className="grid gap-1.5 leading-none flex-1">
                <Label htmlFor="hasEndDate" className="text-sm font-medium">
                  Definir data final
                </Label>
                <p className="text-sm text-gray-500">
                  Até quando a promoção estará disponível
                </p>
                {hasEndDate && (
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="mt-1.5"
                    min={hasStartDate ? startDate : formatDateToISO(new Date())}
                    required
                  />
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="price" className="text-sm font-medium">
                Valor
              </Label>
              <Input
                id="price"
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="mt-1.5"
                min="0"
                step="0.01"
                placeholder="R$ 0,00"
                required
              />
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex h-5 items-center pt-0.5">
                <Checkbox
                  id="isRecurring"
                  checked={isRecurring}
                  onCheckedChange={(checked: boolean) => setIsRecurring(checked)}
                />
              </div>
              <div className="grid gap-1.5 leading-none flex-1">
                <Label htmlFor="isRecurring" className="text-sm font-medium">
                  Assinatura recorrente
                </Label>
                <p className="text-sm text-gray-500">
                  Cobra automaticamente no cartão Mercado Pago a cada ciclo
                </p>
                {isRecurring && (
                  <div className="mt-2 grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="intervalMonths" className="text-xs">
                        Intervalo (meses)
                      </Label>
                      <Input
                        id="intervalMonths"
                        type="number"
                        min={1}
                        value={intervalMonths}
                        onChange={(e) => setIntervalMonths(e.target.value)}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="recurrencePrice" className="text-xs">
                        Preço por ciclo
                      </Label>
                      <Input
                        id="recurrencePrice"
                        type="number"
                        min="0"
                        step="0.01"
                        value={recurrencePrice}
                        onChange={(e) => setRecurrencePrice(e.target.value)}
                        className="mt-1"
                        placeholder="Igual ao valor se vazio"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="submit"
              disabled={isSubmitting || !price || (hasStartDate && !startDate) || (hasEndDate && !endDate)}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Criando...
                </>
              ) : (
                "Criar Promoção"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePromotionDialog;